import React, { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import ROUTES from 'routes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectExistsAuthToken } from 'redux/auth/selectors';
import { getAccount } from 'redux/account/actions';
import { isEmailVerified, selectAccount, selectIsOnboardingComplete } from 'redux/account/selectors';
import { LayoutProps } from 'layouts';
import BaseLayout from '../BaseLayout';
import ErrorPage from 'pages/ErrorPage';

const PrivateLayout: React.FC<LayoutProps> = ({ isFullHeight, isModeration = false }) => {
    const isLoggedIn = useAppSelector(selectExistsAuthToken);
    const hasVerifiedEmail = useAppSelector(isEmailVerified);
    const account = useAppSelector(selectAccount);
    const isOnboardingComplete = useAppSelector(selectIsOnboardingComplete);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const redirectURL = useMemo(() => {
        if (!isLoggedIn || !account) return ROUTES.LOGIN;
        if (!hasVerifiedEmail) return ROUTES.EMAIL_VERIFICATION;
        if (!isOnboardingComplete) {
            return ROUTES.ONBOARDING;
        }

        return null;
    }, [isLoggedIn, hasVerifiedEmail, account, isOnboardingComplete, navigate]);

    useEffect(() => {
        if (redirectURL !== null) {
            navigate(redirectURL);
        }
    }, [isLoggedIn, hasVerifiedEmail, account, isOnboardingComplete, navigate]);

    // Refresh account data
    useEffect(() => {
        dispatch(getAccount());
    }, []);

    if (!redirectURL) {
        if (isModeration && !account?.is_moderator) {
            return (
                <BaseLayout isModerator={account?.is_moderator} authorized={isLoggedIn} isFullHeight={isFullHeight}>
                    <ErrorPage type={401} />
                </BaseLayout>
            );
        }

        return (
            <BaseLayout isModerator={account?.is_moderator} authorized={isLoggedIn} isFullHeight={isFullHeight}>
                <Outlet />
            </BaseLayout>
        );
    }

    return null;
};

export default React.memo(PrivateLayout);
